/**
 * Intercepts the browser back-button so the user is asked to confirm before
 * leaving the app. Also guards accidental tab-close / page-reload via
 * `beforeunload`.
 *
 * Strategy:
 *  1. Push a dummy history entry on startup, so the first Back press lands on
 *     that entry rather than leaving immediately.
 *  2. On `popstate` re-push the guard entry (to restore our position) and show
 *     a modal confirm dialog.
 *  3. If the user confirms, set a flag so the next `popstate` is ignored, then
 *     call `history.go(-2)` to step past both the re-push and the original
 *     guard entry and actually leave.
 *  4. Pressing Escape or "Stay" leaves the guard in place; no navigation occurs.
 */
export function installBackButtonGuard(): void {
  history.pushState({ __nppGuard: true }, "");

  let guardActive = true;

  const onPopstate = (): void => {
    if (!guardActive) return;

    // Re-push immediately so we stay at the same history position
    // while the async confirm dialog is open.
    history.pushState({ __nppGuard: true }, "");

    showLeaveDialog(() => {
      guardActive = false;
      // Step past re-push + original guard entry to actually navigate away.
      history.go(-2);
    });
  };

  window.addEventListener("popstate", onPopstate);

  // Also catch tab-close and page-reload.
  window.addEventListener("beforeunload", (e) => {
    e.preventDefault();
    // returnValue is required for older browsers; value is ignored in modern ones.
    e.returnValue = "";
  });
}

function showLeaveDialog(onLeave: () => void): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-exit-dialog";
  dlg.innerHTML = `
    <div class="npp-exit-dialog-body">
      <h2 class="npp-exit-dialog-title">Leave NullaPad?</h2>
      <p class="npp-exit-dialog-msg">Unsaved changes in open tabs will be lost.</p>
      <div class="npp-exit-dialog-actions">
        <button type="button" class="npp-exit-btn npp-exit-btn--stay">Stay</button>
        <button type="button" class="npp-exit-btn npp-exit-btn--leave">Leave</button>
      </div>
    </div>`;

  document.body.append(dlg);

  const stayBtn = dlg.querySelector<HTMLButtonElement>(".npp-exit-btn--stay")!;
  const leaveBtn = dlg.querySelector<HTMLButtonElement>(".npp-exit-btn--leave")!;

  stayBtn.addEventListener("click", () => dlg.close());
  leaveBtn.addEventListener("click", () => {
    dlg.close();
    onLeave();
  });

  // Prevent Escape from acting as "leave" — it should mean "stay".
  dlg.addEventListener("cancel", (e) => e.preventDefault());
  dlg.addEventListener("close", () => dlg.remove());

  dlg.showModal();
  // "Stay" is the safe default; focus it so Enter = stay.
  stayBtn.focus();
}
